import React, {
  FC, useEffect, useMemo, useState,
} from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { FormControlLabel } from '@mui/material';
import LeftAngle from '../../assets/icons/left-angle.svg';
import RightAngle from '../../assets/icons/right-angle.svg';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import InfoIcon from '../../assets/icons/info-icon.svg';
import EmptyBigCard from '../dashboard/dashboardCards/emptyBigCard';
import { CUSTOM_FIELD_REPORT } from '../../constants/pageIdentifiers';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import SwitchInput from '../inputs/switchInput';
import AdsReportTable from '../../partials/adsReportTable';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import VatDisplayer from '../../partials/VATDisplayer';
import { useOrganizationSettingsStore } from '../../store/organizationSettingsStore';
import OrganizationType from '../enums/organizationTypesEnum';
import ExportButton from '../exportButton/exportButton';
import {
  useGetExportCustomFieldReport,
} from '../../apiHooks/mutations/reportsExportMutations/useGetCustomFieldReferrerTable';
import { useGetCustomFieldReport } from '../../apiHooks/queries/reports/useGetCustomFieldReport';
import {useCustomFieldsStore} from "../../store/customFieldsStore";

export const referrerReportColumns: TableColumnInterface[] = [
  {
    field: 'formSubmissions',
    headerName: 'fsCountSeo',
    cellRenderer: (params) => (
      <span>
        {
            params
              ? (Math.round(params)
                ?.toLocaleString())
              : (0)
        }
      </span>
    ),
  },
  {
    field: 'sales',
    headerName: 'sales',
    cellRenderer: (params) => (
      <span>
        {
            params
              ? (Math.round(params)
                ?.toLocaleString())
              : (0)
        }
      </span>
    ),
  },
  {
    field: 'income',
    headerName: 'income',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'cvRateSeo',
    headerName: 'cvRateSeo',
    cellRenderer: (params, rowObject) => (
      <span>
        {rowObject?.sales && rowObject?.formSubmissions && (
          Math.round((rowObject.sales / (rowObject.formSubmissions || 1)) * 100)
        )}
        %
      </span>
    ),
  },
];

export const eCommerceReferrerReportColumns: TableColumnInterface[] = [
  {
    field: 'sales',
    headerName: 'sales',
    cellRenderer: (params) => (
      <span>
        {
            params
              ? (Math.round(params)
                ?.toLocaleString())
              : (0)
        }
      </span>
    ),
  },
  {
    field: 'income',
    headerName: 'income',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
];

export const referrerReportFixedColumns: TableColumnInterface[] = [
  {
    field: 'customFieldValue',
    headerName: 'Postal Codes',
    cellRenderer: (params: string) => (
      <Tooltip
        arrow
        placement="top"
        title={params}
      >
        <span className="max-w-[190px] truncate 2xl:max-w-[250px]">
          {params === '' ? 'No value provided' : params}
        </span>
      </Tooltip>
    ),
  },
];

const CustomFieldReport: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);
  const [isToggledOriginalReferrer, setIsToggledOriginalReferrer] = useState(false);
  const organizationType = useOrganizationSettingsStore((state) => state.organizationSettings?.organizationType);
  const { selectedCustomField } = useCustomFieldsStore();

  const {
    data: referrerReport,
    isFetching,
    isError,
  } = useGetCustomFieldReport(
    CUSTOM_FIELD_REPORT,
    dashboardFilterDateRange[0],
    dashboardFilterDateRange[1],
    page || 0,
    selectedCustomField?.id,
    selectedCustomField?.name,
  );

  const { mutate: reportDownload } = useGetExportCustomFieldReport({
    from: dashboardFilterDateRange[0],
    to: dashboardFilterDateRange[1],
    original: isToggledOriginalReferrer,
    customFieldId: selectedCustomField?.id ?? null,
    customFieldName: selectedCustomField?.name ?? null,
  });

  const handlePrevPage = () => {
    setPage(page - 1);
  };

  const handleNextPage = () => {
    setPage(page + 1);
  };

  const reportData = referrerReport?.data?.data?.report;
  const hasNextPage = Boolean(referrerReport?.data?.data?.hasNextPage);
  const hasReportData = Array.isArray(reportData) && reportData.length > 0;

  useEffect(() => {
    if (reportData === null && page > 0) {
      setPage(0);
    }
  }, [page, reportData]);

  const toggleReferrer = () => {
    setIsToggledOriginalReferrer(!isToggledOriginalReferrer);
    setPage(0);
  };
  const shouldShowTable = !isFetching && !isError && hasReportData;

  if (!isFetching && !isError && !hasReportData) {
    return null;
  }

  return (
    <div className="flex h-full flex-col text-clip rounded-lg bg-white">
      <div
        className="flex max-h-[47px] items-center justify-between border-b border-dark px-5 py-2"
      >

        <div className="flex items-center gap-4">
          <h2 className="text-lg font-medium leading-[36px] text-primary">
            {t('customFieldReportTitle')}
          </h2>
          <Tooltip title={t('referrerReportInfo')}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
        <div className="flex items-center">
          <div className="flex items-center gap-2">
            <div className="mx-4 flex w-full items-center justify-end gap-2">
              {selectedCustomField ? <div className={"text-xs text-[#828282]"}>{t('filteredByCustomFields')}</div> : <div />}
            </div>
            <div className="flex w-[220px] justify-center rounded-[6px] border py-1">
              <ExportButton fetchDownload={reportDownload} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex h-[400px] w-full flex-col">
        {!isFetching && !isError && (!reportData || reportData.length === 0) && (
          <EmptyBigCard />
        )}
        {isFetching && <LoadingSpinner center color="primary" />}
        {isError && !isFetching && (
          <p>
            {t('errorRetrievingData')}
          </p>
        )}
        {shouldShowTable && (
          <AdsReportTable
            data={referrerReport?.data?.data?.report}
            isFetching={isFetching}
            isError={isError}
            columns={organizationType === OrganizationType.ECOMMERCE
              ? eCommerceReferrerReportColumns
              : referrerReportColumns}
            fixedColumns={referrerReportFixedColumns}
          />
        )}
      </div>
      {!isFetching && !isError && (
        <div className="mb-[22px] mt-3 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={handlePrevPage}
            className={`
              ${page === 0
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10 
            `}
            disabled={page === 0 || isFetching}
          >
            <img src={LeftAngle} alt="" />
          </button>
          <button
            type="button"
            onClick={handleNextPage}
            className={`
              ${!hasNextPage
              ? 'cursor-default opacity-30'
              : 'cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10
            `}
            disabled={!hasNextPage || isFetching}
          >
            <img src={RightAngle} alt="" />
          </button>
        </div>
      )}
    </div>
  );
};
export default CustomFieldReport;
